'use client';

import { createClient } from '@/lib/supabase/client';

interface EmergencyFundContribution {
  projectId: number;
  projectTitle: string;
  donorName: string;
  donorEmail: string;
  donorPhone?: string;
  contributionAmount: number;
  paymentPlatform: string;
  message?: string;
  isAnonymous?: boolean;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export const emergencyFundService = {
  async submitContribution(contribution: EmergencyFundContribution) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('emergency_fund_contributions')
        .insert({
          project_id: contribution.projectId,
          project_title: contribution.projectTitle,
          donor_name: contribution.donorName,
          donor_email: contribution.donorEmail,
          donor_phone: contribution.donorPhone || null,
          contribution_amount: contribution.contributionAmount,
          payment_platform: contribution.paymentPlatform,
          message: contribution.message || null,
          is_anonymous: contribution.isAnonymous || false,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Contribution submission error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getContributionsByProject(projectId: number) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('emergency_fund_contributions')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else if (error.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getTotalContributions() {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('emergency_fund_contributions')
        .select('contribution_amount');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return 0;
        }
      }

      const total = data?.reduce((sum, item) => sum + Number(item?.contribution_amount || 0), 0) || 0;
      return total;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  }
};